import Vue from 'vue';
import { $t } from '@/i18n';
import { getAIPageContextInstance } from '@/utils/global/api';
import getXyHeaders from '@/utils/getXyHeaders';
import { message } from 'tdesign-vue';
import { delay } from '@/utils/delay';
import getXyManagePathPrefix from '@/utils/getXyManagePathPrefix';
import { addPathPrefix } from '@/utils/path';
import { clamp, debounce, memoize } from 'lodash';
let wujiFetchAccessPromise = null;
const hashMismatchState = new Vue({
    data() {
        return {
            stateKey: '',
            toastVisible: false,
            confirmStatus: 'idle',
            confirmResolvers: null,
            confirmPromise: null,
            modalVM: null,
        };
    },
    created() {
        this.$watch(
        // dev模式下，这个是vue响应式的
        () => window.__gogoDebugHijack?.backendHash, () => this.resetAll());
    },
    methods: {
        ensureStateKey(editorHash, deployedHash) {
            const key = `${editorHash}:${deployedHash}`;
            console.log('ensureStateKey', key, this.stateKey, key !== this.stateKey);
            if (key !== this.stateKey) {
                this.resetAll();
                this.stateKey = key;
            }
        },
        resetAll() {
            this.stateKey = '';
            this.toastVisible = false;
            if (this.confirmStatus === 'confirming' && this.confirmResolvers) {
                this.confirmResolvers.reject(new Error('Hash changed, state reset'));
            }
            this.confirmStatus = 'idle';
            this.confirmResolvers = null;
            this.confirmPromise = null;
            if (this.modalVM) {
                const vm = this.modalVM;
                this.modalVM = null;
                setTimeout(() => { vm.$el?.remove(); vm.$destroy(); }, 0);
            }
        },
        handleReadRequest() {
            this.toastVisible = true;
        },
        handleWriteRequest(projectId, expectedHash) {
            if (this.confirmStatus === 'confirmed')
                return Promise.resolve('confirmed');
            if (this.confirmStatus === 'rejected')
                return Promise.resolve('rejected');
            if (this.confirmStatus === 'confirming' && this.confirmPromise)
                return this.confirmPromise;
            this.toastVisible = true;
            this.confirmStatus = 'confirming';
            this.confirmPromise = new Promise((resolve, reject) => {
                this.confirmResolvers = { resolve, reject };
                this._showModal(projectId, expectedHash);
            });
            return this.confirmPromise;
        },
        _showModal(projectId, expectedHash) {
            import('./WaitBackendReadyLayout.vue')
                .then(m => m.default)
                .then((Modal) => {
                if (this.confirmStatus !== 'confirming')
                    return;
                const container = document.createElement('div');
                document.body.appendChild(container);
                const vm = new Vue({
                    render: h => h(Modal, {
                        props: { projectId, expectedHash, autoPolling: true },
                        on: {
                            answer: (ans, accessResult) => {
                                this._onModalAnswer(ans, accessResult);
                            },
                        },
                    }),
                });
                vm.$mount(container);
                this.modalVM = vm;
            });
        },
        _onModalAnswer(ans, accessResult) {
            if (this.confirmStatus !== 'confirming')
                return;
            const resolvers = this.confirmResolvers;
            if (this.modalVM) {
                const vm = this.modalVM;
                this.modalVM = null;
                setTimeout(() => { vm.$el?.remove(); vm.$destroy(); }, 0);
            }
            this.confirmResolvers = null;
            if (ans === 'request-anyway') {
                this.confirmStatus = 'confirmed';
                resolvers?.resolve('confirmed');
            }
            else {
                this.confirmStatus = 'rejected';
                resolvers?.resolve('rejected');
            }
        },
    },
    render(h) {
        if (!this.toastVisible)
            return h('div');
        return h('div', {
            style: {
                position: 'fixed',
                top: '0',
                left: '0',
                right: '0',
                zIndex: 999,
                background: '#fff3cd',
                color: '#856404',
                borderBottom: '1px solid #ffc107',
                padding: '8px 16px',
                textAlign: 'center',
                fontSize: '14px',
                opacity: '0.7',
            },
        }, [$t('k_d5f96348')]);
    },
});
hashMismatchState.$mount(document.body.appendChild(document.createElement('div')));
// ============ Access Token ============
function fetchAccessDirectly(projectId, options = {}) {
    let url = `${location.origin}${addPathPrefix(getXyManagePathPrefix())}/ai-project/access?projectId=${projectId}`;
    if (options.firstFlight) {
        url += '&firstFlight=true';
    }
    return fetch(url, { headers: getXyHeaders() })
        .then(res => res.json())
        .then(X => X?.data);
}
export function resetWujiFetchAccessPromise() {
    wujiFetchAccessPromise = null;
    hashMismatchState.resetAll();
}
function createNewWujiFetchAccessPromise() {
    const w = getAIPageContextInstance();
    const { env, projectId } = w;
    const isDev = env === 'dev';
    const clear = () => {
        if (wujiFetchAccessPromise === promise)
            wujiFetchAccessPromise = null;
    };
    const promise = (async () => {
        const cleanups = [];
        const deadline = Date.now() + 120e3;
        let hitDeploying = false;
        let answer = null;
        try {
            while (Date.now() < deadline) {
                // 第一次查询需要立即返回，不然服务器要等很久才出接口，掩盖了 deploying 的部署状态
                const access = await fetchAccessDirectly(projectId, { firstFlight: !hitDeploying });
                if (access?.deployStatus === 'deploying') {
                    if (!hitDeploying) {
                        hitDeploying = true;
                        if (isDev) {
                            message.loading($t('k_8f78fe2d')).then((loading) => {
                                cleanups.push(() => loading.close());
                            });
                        }
                    }
                    await delay(500);
                    continue;
                }
                if (access?.deployStatus === 'failed') {
                    const msg = $t('k_5ddc9a0e');
                    message.error(msg);
                    throw new Error(msg);
                }
                if (!access?.token)
                    throw new Error('Failed to get wujiFetch access.' + (hitDeploying ? ' Server is still deploying, please refresh...' : ''));
                answer = access;
                break;
            }
            // 前面已经 await 过了，promise 已经 resolved，所以可以赋值 result
            // @ts-ignore
            promise.result = answer;
            return answer;
        }
        finally {
            cleanups.forEach(c => c());
            if (!answer?.expiresAt)
                clear();
            else
                setTimeout(clear, clamp(answer.expiresAt - Date.now(), 1000, 600e3));
        }
    })();
    return promise;
}
let devPageFocusEventMounted = false;
export async function getWujiFetchAccess(options) {
    const w = getAIPageContextInstance();
    const { env, projectId } = w;
    const isDev = env === 'dev';
    const isReadonlyAccess = options.method === 'OPTIONS' || options.method === 'GET' || options.method === 'HEAD';
    const hashFromGogoEditor = window.__gogoDebugHijack?.backendHash;
    // 先请求后台获取 access token 和部署（创建 Promise 但是不一定马上有结果）
    if (!wujiFetchAccessPromise) {
        wujiFetchAccessPromise = createNewWujiFetchAccessPromise();
    }
    // 开发模式特殊逻辑：每次页面 focus 的时候都重新 fetch，检查 hash 是否一致 -- 影响下一次请求
    if (isDev && !devPageFocusEventMounted) {
        devPageFocusEventMounted = true;
        window.addEventListener('focus', debounce(() => {
            const old = wujiFetchAccessPromise;
            const n = createNewWujiFetchAccessPromise();
            n.then(() => {
                if (old === wujiFetchAccessPromise)
                    wujiFetchAccessPromise = n;
            });
        }, 3000));
    }
    // 开发模式下需要检查 hash 是否一致，不一致则显示 toast 或者 确认弹窗
    if (isDev && hashFromGogoEditor) {
        const res = await wujiFetchAccessPromise;
        if (!res)
            return null;
        const isUpToDate = res.hash === hashFromGogoEditor;
        if (isUpToDate)
            return res;
        // 不一致，需要显示 toast 或者 确认弹窗
        hashMismatchState.ensureStateKey(hashFromGogoEditor, res.hash);
        if (isReadonlyAccess) {
            hashMismatchState.handleReadRequest();
        }
        else {
            const freshAccess = await hashMismatchState.handleWriteRequest(projectId, hashFromGogoEditor);
            if (freshAccess === 'confirmed')
                return res;
            if (freshAccess === 'rejected')
                throw new Error('User cancelled the request');
        }
    }
    // 正常情况，直接返回结果
    const res = await wujiFetchAccessPromise;
    return res;
}
export const doubleCheckLoginStatusAndDisplayLoginIfNeeded = memoize(async () => {
    try {
        const e = await getWujiFetchAccess({ method: 'OPTIONS' });
        if (!e)
            return;
        const { ssoCheckUrl } = e;
        if (!ssoCheckUrl)
            return; // 没有 SSO 检查 URL，直接返回
        const healthCheckRes = await fetch(`${ssoCheckUrl}/health`, {
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
            },
        });
        if (healthCheckRes.status === 200)
            return; // 健康
        // 不健康，需要重新登录。
        const iframeUrl = `${ssoCheckUrl}/login/`;
        // 创建 wrapper 和 iframe
        const wrapper = document.createElement('div');
        wrapper.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
    `;
        const closeButton = document.createElement('button');
        closeButton.innerHTML = '×';
        closeButton.style.cssText = `
      position: absolute;
      top: 20px;
      right: 20px;
      width: 32px;
      height: 32px;
      border: none;
      border-radius: 50%;
      background-color: rgba(255, 255, 255, 0.9);
      color: #333;
      font-size: 24px;
      line-height: 1;
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1;
    `;
        closeButton.onmouseover = () => {
            closeButton.style.backgroundColor = 'rgba(255, 255, 255, 1)';
            closeButton.style.transform = 'scale(1.1)';
        };
        closeButton.onmouseout = () => {
            closeButton.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
            closeButton.style.transform = 'scale(1)';
        };
        const contentContainer = document.createElement('div');
        contentContainer.style.cssText = `
      position: relative;
      width: 600px;
      height: 400px;
    `;
        const iframe = document.createElement('iframe');
        iframe.src = iframeUrl;
        iframe.style.cssText = `
      width: 100%;
      height: 100%;
      border: none;
      border-radius: 8px;
      background-color: white;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    `;
        contentContainer.appendChild(iframe);
        contentContainer.appendChild(closeButton);
        wrapper.appendChild(contentContainer);
        document.body.appendChild(wrapper);
        // 等待登录成功的消息
        return await new Promise((resolve) => {
            const handleMessage = (event) => {
                if (event.data?.type === 'sso-login-ok'
                    && event.data?.source === 'gogo-scf') {
                    // 登录成功，清理 UI
                    window.removeEventListener('message', handleMessage);
                    document.body.removeChild(wrapper);
                    finalize();
                    resolve('sso-login-done');
                    message.success($t('k_2d2025c4'));
                }
            };
            const finalize = () => {
                window.removeEventListener('message', handleMessage);
                if (document.body.contains(wrapper)) {
                    document.body.removeChild(wrapper);
                }
            };
            const abort = () => {
                finalize();
                resolve('aborted');
            };
            // 绑定关闭按钮点击事件
            closeButton.addEventListener('click', abort);
            // 点击遮罩层关闭（点击 wrapper 但不是 contentContainer）
            wrapper.addEventListener('click', (e) => {
                if (e.target === wrapper) {
                    abort();
                }
            });
            window.addEventListener('message', handleMessage);
        });
    }
    finally {
        doubleCheckLoginStatusAndDisplayLoginIfNeeded.cache.clear?.();
    }
}, () => 'doubleCheckLoginStatusAndDisplayLoginIfNeeded');
